import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { SquarePen, Trash } from "lucide-react";
import api from "../../utils/api";

function HolidayCalendar() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [holidayName, setHolidayName] = useState("");
  const [holidays, setHolidays] = useState([]);
  const [editHoliday, setEditHoliday] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const holidaysPerPage = 7;

  const indexOfLast = currentPage * holidaysPerPage;
  const indexOfFirst = indexOfLast - holidaysPerPage;
  const currentHolidays = holidays.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(holidays.length / holidaysPerPage);

  const baseUrl = process.env.REACT_APP_API_URL;

  const fetchHolidays = async () => {
    try {
      const res = await api.get(`/api/holidays`);
      setHolidays(res.data);
    } catch (err) {
      console.error("Error fetching holidays", err);
    }
  };

  const handleAddHoliday = async () => {
    if (!holidayName.trim()) {
      toast.warning("Please enter holiday name.");
      return;
    }

    try {

       const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

      await api.post(`/api/holidays`, {
        holidayDate: formattedDate,
        holidayName,
      });

      console.log("Added holiday:", { holidayDate: selectedDate, holidayName });
      toast.success("Holiday added!");
      setHolidayName("");
      fetchHolidays();
    } catch (err) {
      toast.error("Failed to add holiday.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this holiday?")) {
      try {
        await api.delete(`/api/holidays/${id}`);
        toast.success("Holiday deleted.");
        fetchHolidays();
      } catch (err) {
        toast.error("Error deleting holiday.");
      }
    }
  };

  const handleEditSubmit = async () => {
    try {
      await api.put(`/api/holidays/${editHoliday.id}`, {
        holidayDate: editHoliday.holidayDate,
        holidayName: editHoliday.holidayName,
      });
      toast.success("Holiday updated.");
      setShowEditModal(false);
      fetchHolidays();
    } catch (err) {
      toast.error("Update failed.");
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Holiday Calendar</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Calendar + Form */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <Calendar
            onChange={setSelectedDate}
            value={selectedDate}
            className="text-sm border rounded-xl p-2 mx-auto"
            tileClassName="hover:bg-blue-100"
          />

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-gray-600 text-sm">Selected Date</label>
              <input
                type="text"
                value={selectedDate.toDateString()}
                readOnly
                className="w-full px-4 py-2 bg-gray-100 border rounded-full"
              />
            </div>
            <div>
              <label className="text-gray-600 text-sm">Holiday Name</label>
              <input
                type="text"
                value={holidayName}
                onChange={(e) => setHolidayName(e.target.value)}
                className="w-full px-4 py-2 border rounded-full"
                placeholder="e.g. Republic Day"
              />
            </div>
            <button
              onClick={handleAddHoliday}
              className="w-full mt-2 py-2 bg-slate-800 text-white rounded-full hover:bg-slate-700 transition"
            >
              ➕ Add Holiday
            </button>
          </div>
        </div>

        {/* Holiday List */}
        <motion.div
          className="bg-white rounded-2xl shadow-md overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold text-slate-600">Sl. No</th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-600">Date</th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-600">Holiday Name</th>
                  <th className="px-6 py-4 text-center font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {currentHolidays.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-8 text-slate-500">
                      No holidays found.
                    </td>
                  </tr>
                ) : (
                  currentHolidays.map((holiday, index) => (
                    <tr key={holiday.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-slate-600">{indexOfFirst + index + 1}</td>
                      <td className="px-6 py-4">{formatDate(holiday.holidayDate)}</td>
                      <td className="px-6 py-4">{holiday.holidayName}</td>
                      <td className="px-6 py-4 text-center space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setEditHoliday({ ...holiday });
                            setShowEditModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <SquarePen />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDelete(holiday.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash />
                        </motion.button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {holidays.length > holidaysPerPage && (
            <div className="px-6 py-4 border-t border-slate-100">
              <Stack spacing={2} className="flex justify-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  variant="outlined"
                  shape="rounded"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#475569",
                      borderColor: "#e2e8f0",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#475569 !important",
                      color: "#fff",
                    },
                  }}
                />
              </Stack>
            </div>
          )}
        </motion.div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editHoliday && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowEditModal(false);
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white p-6 rounded-2xl max-w-md w-full mx-4 shadow-xl"
          >
            <h2 className="text-xl font-bold mb-4 text-center">Edit Holiday</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-600">Date</label>
                <input
                  type="text"
                  value={editHoliday.holidayDate}
                  readOnly
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600">Holiday Name</label>
                <input
                  type="text"
                  value={editHoliday.holidayName}
                  onChange={(e) =>
                    setEditHoliday({ ...editHoliday, holidayName: e.target.value })
                  }
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 rounded-full bg-gray-200 hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  className="px-4 py-2 rounded-full bg-slate-800 text-white hover:bg-slate-700"
                >
                  Update
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default HolidayCalendar;
